import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  TrendingUp, ShoppingBag, Users as UsersIcon, Package, IndianRupee,
  AlertTriangle, ArrowUpRight, Crown
} from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, BarChart, Bar, Legend
} from "recharts";
import { api } from "@/api/client";

const STATUS_COLORS = {
  Confirmed: "#14573C",
  Processing: "#D59A23",
  Shipped: "#1c6f3d",
  Delivered: "#0c3a22",
  Cancelled: "#9b2c2c",
};
const METHOD_COLORS = ["#14573C", "#D59A23", "#584A2E"];

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/admin/stats")
      .then(({ data }) => setData(data))
      .catch((e) => setErr(e?.response?.data?.detail || "Failed to load stats"));
  }, []);

  if (err) return <div className="text-red-700">{err}</div>;
  if (!data) return <Skeleton />;

  const t = data.totals;
  const tiles = [
    { label: "Lifetime Revenue", value: `₹${t.revenue.toLocaleString("en-IN")}`, sub: `Last 30d: ₹${data.last_30d.revenue.toLocaleString("en-IN")}`, icon: IndianRupee, accent: "var(--green)" },
    { label: "Total Orders", value: t.orders, sub: `Last 30d: ${data.last_30d.orders}`, icon: ShoppingBag, accent: "var(--gold)" },
    { label: "Customers", value: t.users, sub: `Avg. order ₹${t.aov.toLocaleString("en-IN")}`, icon: UsersIcon, accent: "var(--green-deep)" },
    { label: "Products in Catalog", value: t.products, sub: `${data.low_stock.length} low-stock alert(s)`, icon: Package, accent: "var(--earth)" },
  ];

  const fmtDay = (d) => {
    const dt = new Date(d);
    return dt.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
  };

  return (
    <div className="space-y-7" data-testid="admin-dashboard">
      {/* Tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {tiles.map((tile) => (
          <div key={tile.label} className="bg-white border border-[#e5dec9] rounded-xl p-5 relative overflow-hidden">
            <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full opacity-10" style={{ background: tile.accent }} />
            <div className="kbd text-[0.6rem] text-[var(--ink-3)]">{tile.label}</div>
            <div className="font-serif-display text-3xl mt-2 text-[var(--ink)]">{tile.value}</div>
            <div className="flex items-center gap-1.5 mt-2 text-[11px] text-[var(--ink-3)]">
              <tile.icon size={12} className="text-[var(--gold)]" />
              {tile.sub}
            </div>
          </div>
        ))}
      </div>

      {/* Revenue + Status */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
        <div className="xl:col-span-2 bg-white border border-[#e5dec9] rounded-xl p-5">
          <div className="flex items-center justify-between mb-1">
            <div>
              <div className="kbd text-[0.6rem] text-[var(--ink-3)]">Revenue · Last 30 Days</div>
              <h3 className="font-serif-display text-xl text-[var(--ink)]">Daily Performance</h3>
            </div>
            <div className="inline-flex items-center gap-1.5 text-[11px] text-[var(--green)] font-semibold">
              <TrendingUp size={13} /> ₹{data.last_7d.revenue.toLocaleString("en-IN")} last 7 days
            </div>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={data.revenue_by_day} margin={{ top: 12, right: 12, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="revG" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#14573C" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#14573C" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee5d2" vertical={false} />
              <XAxis dataKey="date" tickFormatter={fmtDay} stroke="#7d6c4d" fontSize={11} interval={4} />
              <YAxis stroke="#7d6c4d" fontSize={11} tickFormatter={(v) => `₹${v}`} />
              <Tooltip
                contentStyle={{ borderRadius: 8, border: "1px solid #e5dec9", fontSize: 12 }}
                formatter={(v, name) => name === "revenue" ? [`₹${v.toLocaleString("en-IN")}`, "Revenue"] : [v, name]}
                labelFormatter={fmtDay}
              />
              <Area type="monotone" dataKey="revenue" stroke="#14573C" strokeWidth={2.4} fill="url(#revG)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white border border-[#e5dec9] rounded-xl p-5">
          <div className="kbd text-[0.6rem] text-[var(--ink-3)]">Orders by Status</div>
          <h3 className="font-serif-display text-xl text-[var(--ink)]">Fulfilment Mix</h3>
          {data.orders_by_status.length === 0 ? (
            <p className="text-xs text-[var(--ink-3)] mt-10 text-center">No orders yet.</p>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={data.orders_by_status} dataKey="count" nameKey="status" outerRadius={88} innerRadius={50} paddingAngle={3}>
                  {data.orders_by_status.map((s, i) => (
                    <Cell key={i} fill={STATUS_COLORS[s.status] || "#888"} />
                  ))}
                </Pie>
                <Tooltip formatter={(v, n) => [v, "Orders"]} />
                <Legend iconType="circle" wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Top products + Payment methods + Low stock */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
        <div className="xl:col-span-2 bg-white border border-[#e5dec9] rounded-xl p-5">
          <div className="kbd text-[0.6rem] text-[var(--ink-3)]">Top 5 Products</div>
          <h3 className="font-serif-display text-xl text-[var(--ink)] flex items-center gap-2">
            <Crown size={16} className="text-[var(--gold)]" /> Bestsellers
          </h3>
          {data.top_products.length === 0 ? (
            <p className="text-xs text-[var(--ink-3)] mt-10 text-center">Sales data will populate as orders come in.</p>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={data.top_products} layout="vertical" margin={{ top: 8, right: 12, left: 12, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee5d2" horizontal={false} />
                <XAxis type="number" stroke="#7d6c4d" fontSize={11} />
                <YAxis type="category" dataKey="name" stroke="#7d6c4d" fontSize={11} width={140} />
                <Tooltip
                  contentStyle={{ borderRadius: 8, border: "1px solid #e5dec9", fontSize: 12 }}
                  formatter={(v, n) => n === "qty" ? [v, "Units sold"] : [`₹${v}`, "Revenue"]}
                />
                <Bar dataKey="qty" fill="#D59A23" radius={[0, 4, 4, 0]} barSize={18} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="bg-white border border-[#e5dec9] rounded-xl p-5">
          <div className="kbd text-[0.6rem] text-[var(--ink-3)]">Payment Methods</div>
          <h3 className="font-serif-display text-xl text-[var(--ink)]">Split</h3>
          {data.payment_methods.length === 0 ? (
            <p className="text-xs text-[var(--ink-3)] mt-10 text-center">No payments yet.</p>
          ) : (
            <ul className="mt-4 space-y-3">
              {data.payment_methods.map((p, i) => (
                <li key={p.method} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: METHOD_COLORS[i % METHOD_COLORS.length] }} />
                    <span className="font-medium text-sm uppercase tracking-widest text-[var(--ink)]">{p.method}</span>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-semibold text-[var(--green)]">₹{p.amount.toLocaleString("en-IN")}</div>
                    <div className="kbd text-[0.55rem] text-[var(--ink-3)]">{p.count} orders</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Recent orders + Low stock */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
        <div className="xl:col-span-2 bg-white border border-[#e5dec9] rounded-xl p-5">
          <div className="flex items-center justify-between">
            <h3 className="font-serif-display text-xl text-[var(--ink)]">Recent Orders</h3>
            <Link to="/admin/orders" className="text-xs text-[var(--green)] font-semibold inline-flex items-center gap-1 hover:gap-1.5 transition-all">
              View all <ArrowUpRight size={12} />
            </Link>
          </div>
          {data.recent_orders.length === 0 ? (
            <p className="text-sm text-[var(--ink-3)] mt-6">No orders yet.</p>
          ) : (
            <table className="w-full mt-4 text-sm">
              <thead className="text-[var(--ink-3)] uppercase tracking-widest text-[10px]">
                <tr><th className="text-left py-2">Order</th><th className="text-left">Customer</th><th className="text-right">Total</th><th className="text-right">Status</th></tr>
              </thead>
              <tbody>
                {data.recent_orders.map((o) => (
                  <tr key={o.id} className="border-t border-[#f0e7d3]">
                    <td className="py-2.5 font-semibold text-[var(--ink)]">{o.order_no}</td>
                    <td className="text-[var(--ink-2)]">{o.user_email}</td>
                    <td className="text-right font-semibold text-[var(--green)]">₹{o.total}</td>
                    <td className="text-right">
                      <span className="text-[10px] uppercase tracking-widest font-semibold px-2 py-1 rounded-full border" style={{ color: STATUS_COLORS[o.status] || "#444", borderColor: STATUS_COLORS[o.status] || "#aaa" }}>
                        {o.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="bg-white border border-[#e5dec9] rounded-xl p-5">
          <div className="kbd text-[0.6rem] text-[var(--ink-3)]">Inventory</div>
          <h3 className="font-serif-display text-xl text-[var(--ink)] flex items-center gap-2">
            <AlertTriangle size={16} className="text-[#9b2c2c]" /> Low Stock
          </h3>
          {data.low_stock.length === 0 ? (
            <p className="text-sm text-[var(--ink-3)] mt-5">All products are well stocked.</p>
          ) : (
            <ul className="mt-4 space-y-2.5">
              {data.low_stock.map((p) => (
                <li key={p.id} className="flex items-center justify-between text-sm">
                  <span className="text-[var(--ink)] truncate">{p.name}</span>
                  <span className={`kbd text-[0.6rem] font-bold ${p.stock <= 5 ? "text-red-600" : "text-[var(--gold)]"}`}>
                    {p.stock} left
                  </span>
                </li>
              ))}
            </ul>
          )}
          <Link to="/admin/products" className="text-xs text-[var(--green)] font-semibold inline-flex items-center gap-1 mt-4 hover:gap-1.5 transition-all">
            Manage products <ArrowUpRight size={12} />
          </Link>
        </div>
      </div>
    </div>
  );
}

function Skeleton() {
  return (
    <div className="space-y-5 animate-pulse">
      <div className="grid grid-cols-4 gap-4">{[0,1,2,3].map(i => <div key={i} className="h-28 bg-white rounded-xl" />)}</div>
      <div className="h-72 bg-white rounded-xl" />
      <div className="h-64 bg-white rounded-xl" />
    </div>
  );
}
